import { useState } from 'react';
import SeoHead from '@/components/base/SeoHead';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutBreadcrumb = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2022', title: '产品立项', desc: '91浏览器项目正式立项，核心团队组建完成，开启自研浏览器引擎的研发之路。' },
  { year: '2023', title: '首个内测版发布', desc: '完成闪电渲染引擎1.0开发，发布首个内测版本，获得首批1000名种子用户的积极反馈。' },
  { year: '2024', title: '正式版上线', desc: '91浏览器v1.0正式版发布，同步上线Windows和Android双平台，上线首月用户突破500万。' },
  { year: '2025', title: '突破1亿用户', desc: '全平台用户突破1亿，完成macOS和iOS版本开发，实现四大平台全覆盖，获得多项行业大奖。' },
  { year: '2026', title: '全球化布局', desc: '发布v3.8版本，引擎性能提升40%，用户突破2亿，启动国际化战略。' },
];

const teamValues = [
  { icon: 'ri-user-heart-line', title: '用户至上', desc: '每一个功能设计都从用户真实需求出发，持续优化体验细节。' },
  { icon: 'ri-shield-star-line', title: '隐私优先', desc: '将用户隐私保护作为产品基石，数据加密和本地化存储是基本要求。' },
  { icon: 'ri-rocket-2-line', title: '极致性能', desc: '不断突破技术边界，追求毫秒级的加载速度优化，让等待成为过去。' },
  { icon: 'ri-open-arm-line', title: '开放共赢', desc: '拥抱开源社区，与开发者共建浏览器生态，让创新惠及更多用户。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '自由职业者',
    content: '用了91浏览器快两年了，最让我满意的就是它的速度。以前打开淘宝、京东这种大站总要等好一会，现在基本是秒开。广告拦截也很给力，终于不用被弹窗广告烦了。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '大学教师',
    content: '作为教师，我经常需要在多台设备间切换工作。91浏览器的多端同步功能让我的书签和密码在所有设备上自动同步，再也不用一个个手动输入了，大大提高了工作效率。',
    rating: 5,
  },
  {
    name: '王同学',
    role: '在校研究生',
    content: '我是通过同学推荐开始用91浏览器的。隐私保护模式让我在做课题研究时可以不留痕迹，查阅资料也不用担心被追踪。主题商店还有很多好看的主题可以换。',
    rating: 5,
  },
  {
    name: '赵经理',
    role: 'IT项目经理',
    content: '从专业角度看，91浏览器的底层技术实力很强。自研的渲染引擎在性能测试中表现出色，尤其在国内网络环境下的优化做得很到位。我已经推荐给整个团队使用了。',
    rating: 4,
  },
  {
    name: '陈女士',
    role: '自媒体创作者',
    content: '做自媒体需要经常浏览各种网站找素材，91浏览器的广告拦截和隐私保护帮我省了不少心。而且手机版和电脑版切换很流畅，在外面用手机收集的素材回家就能在电脑上继续编辑。',
    rating: 5,
  },
  {
    name: '刘先生',
    role: '企业管理者',
    content: '我们公司已经全员使用91浏览器，主要是看中它的安全性和稳定性。密码管理器功能让员工不再需要记住一堆密码，配合生物识别解锁，既安全又方便。客服响应也很快。',
    rating: 5,
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于91浏览器',
  description: '91浏览器团队致力于打造中国最优秀的浏览器产品，以极速、安全、易用为核心理念，为用户提供卓越的上网体验。',
  url: 'https://mingcidianzi162.cn/about',
  inLanguage: 'zh-CN',
};

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const companyAltValue = (formData.get('company_alt') as string || '').trim();
    if (companyAltValue) {
      setFormState('success');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9pkcrti2vhsdh2d3690', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.toLowerCase().includes('spam')) {
          setFormState('success');
          form.reset();
        } else {
          setFormState('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormState('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SeoHead
        title="关于91浏览器 - 品牌介绍·用户口碑·联系方式"
        description="了解91浏览器品牌故事与发展历程。91浏览器团队致力于打造中国最优秀的浏览器产品，以极速、安全、易用为核心理念，服务超过2亿用户。查看用户真实口碑与联系方式。"
        keywords="91浏览器关于我们,91浏览器品牌介绍,91浏览器口碑,91浏览器联系方式,91浏览器团队"
        canonicalUrl="/about"
        schema={aboutSchema}
        schemaType="WebPage"
      />

      <main className="min-h-screen bg-background-50 pt-24 pb-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={aboutBreadcrumb} />
          </div>

          <section className="mb-16">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">关于91浏览器</h1>
            <p className="text-foreground-600 text-sm max-w-3xl leading-relaxed">
              91浏览器诞生于2022年，由一群热爱技术、追求极致体验的工程师创立。
              我们相信，浏览器不只是一个工具，更是连接人与信息的桥梁。
              91浏览器以<strong>极速</strong>、<strong>安全</strong>、<strong>易用</strong>为核心理念，
              致力于为每一位用户提供卓越的上网体验。
            </p>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8">发展历程</h2>
            <div className="relative">
              <div className="absolute left-5 top-2 bottom-2 w-px bg-background-200 hidden md:block"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={m.year} className="flex gap-5 relative">
                    <div className="hidden md:flex w-10 h-10 rounded-full bg-primary-500 text-background-50 items-center justify-center flex-shrink-0 z-10 text-xs font-bold">
                      {i + 1}
                    </div>
                    <div className="flex-1 bg-background-100 rounded-xl p-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-500 text-background-50">{m.year}</span>
                        <h3 className="text-base font-semibold text-foreground-950">{m.title}</h3>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">团队理念</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamValues.map((v) => (
                <div key={v.title} className="bg-background-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                    <i className={`${v.icon} text-primary-600 text-lg`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{v.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.name} className="bg-background-100 rounded-xl p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-sm ${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center">
                      <span className="text-xs font-semibold text-accent-700">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">联系我们</h2>
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">电子邮箱</p>
                      <p className="text-sm text-foreground-600">support@91browser.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">客服电话</p>
                      <p className="text-sm text-foreground-600">400-888-9191</p>
                      <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">公司地址</p>
                      <p className="text-sm text-foreground-600">北京市海淀区中关村科技园</p>
                      <p className="text-sm text-foreground-600">创新大厦A座12层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-950">官方微信</p>
                      <p className="text-sm text-foreground-600">搜索"91浏览器"关注公众号</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-xl p-6">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">商务合作</h3>
                  <p className="text-sm text-foreground-600 mb-4">
                    如果您有商务合作、媒体采访、战略投资等方面的需求，欢迎通过以下方式联系我们：
                  </p>
                  <div className="space-y-2 text-sm">
                    <p className="text-foreground-700">
                      <strong>商务邮箱：</strong>business@91browser.com
                    </p>
                    <p className="text-foreground-700">
                      <strong>媒体联系：</strong>pr@91browser.com
                    </p>
                    <p className="text-foreground-700">
                      <strong>加入我们：</strong>hr@91browser.com
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-6">在线留言</h2>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-xl p-6 space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-900 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-900 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-900 mb-1.5">电话</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的电话（选填）"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-900 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <div className="absolute opacity-0 pointer-events-none" style={{ position: 'absolute', left: '-9999px' }}>
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  {formState === 'success' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                      <i className="ri-checkbox-circle-fill text-green-500"></i>
                      留言已成功提交，我们会尽快与您联系！
                    </div>
                  )}

                  {formState === 'error' && (
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                      <i className="ri-error-warning-fill text-red-500"></i>
                      {formError}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formState === 'submitting'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {formState === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        提交中...
                      </span>
                    ) : '提交留言'}
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}